import { Injectable, inject, ErrorHandler, NgZone } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { NotificationService } from './notification.service';

/**
 * Parsed error information
 */
export interface ParsedError {
  message: string;
  code?: string;
  status?: number;
  details?: unknown;
  isNetworkError: boolean;
  isAuthError: boolean;
  isValidationError: boolean;
}

/**
 * ErrorHandlerService
 *
 * Centralized error handling service for the application.
 * Provides consistent error parsing, logging, and user notification.
 *
 * @example
 * ```typescript
 * // In service or component
 * private errorHandler = inject(ErrorHandlerService);
 *
 * // Handle HTTP error
 * this.http.get('/api/data').subscribe({
 *   error: (err) => this.errorHandler.handleHttpError(err)
 * });
 *
 * // Parse error for custom handling
 * const parsed = this.errorHandler.parseError(error);
 * if (parsed.isValidationError) {
 *   // Handle validation errors differently
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {
  private notification = inject(NotificationService);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  /**
   * Handle an HTTP error with user notification
   * @param error - The HTTP error response
   * @param context - Optional context for better error messages
   */
  handleHttpError(error: HttpErrorResponse, context?: string): void {
    const parsed = this.parseHttpError(error);

    // Log error for debugging
    this.logError(error, context);

    // Handle auth errors specially
    if (parsed.isAuthError) {
      this.handleAuthError(error);
      return;
    }

    // Show user-friendly notification
    const message = context
      ? `${context}: ${parsed.message}`
      : parsed.message;

    this.notification.error(message);
  }

  /**
   * Handle any type of error
   * @param error - The error to handle
   * @param context - Optional context for better error messages
   */
  handleError(error: unknown, context?: string): void {
    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error, context);
      return;
    }

    const parsed = this.parseError(error);
    this.logError(error, context);

    const message = context
      ? `${context}: ${parsed.message}`
      : parsed.message;

    this.notification.error(message);
  }

  /**
   * Parse an HTTP error into structured information
   */
  parseHttpError(error: HttpErrorResponse): ParsedError {
    const base: ParsedError = {
      message: 'An unexpected error occurred',
      status: error.status,
      isNetworkError: false,
      isAuthError: false,
      isValidationError: false,
    };

    // Network error (no connection)
    if (error.status === 0) {
      return {
        ...base,
        message: 'Unable to connect to server. Please check your internet connection.',
        isNetworkError: true,
      };
    }

    // Always try to extract backend message first
    const backendMessage = this.extractBackendMessage(error);

    // Authentication errors
    if (error.status === 401) {
      return {
        ...base,
        message: backendMessage || 'Your session has expired. Please log in again.',
        isAuthError: true,
      };
    }

    if (error.status === 403) {
      return {
        ...base,
        message: backendMessage || 'You do not have permission to perform this action.',
        isAuthError: true,
      };
    }

    // Validation errors
    if (error.status === 400 || error.status === 422) {
      const validationMessage = backendMessage || this.extractValidationMessage(error);
      return {
        ...base,
        message: validationMessage,
        isValidationError: true,
        details: error.error,
      };
    }

    // Not found — prefer backend message
    if (error.status === 404) {
      return {
        ...base,
        message: backendMessage || 'The requested resource was not found.',
      };
    }

    // Server errors — prefer backend message
    if (error.status >= 500) {
      return {
        ...base,
        message: backendMessage || 'A server error occurred. Please try again later.',
      };
    }

    return {
      ...base,
      message: backendMessage || base.message,
    };
  }

  /**
   * Extract message directly from backend response body
   */
  private extractBackendMessage(error: HttpErrorResponse): string | null {
    const body = error.error;
    if (!body) return null;
    if (typeof body === 'object' && body.message) return body.message;
    if (typeof body === 'object' && body.title) return body.title;
    if (typeof body === 'string' && body.length > 0 && body.length < 500) return body;
    return null;
  }

  /**
   * Parse any error into structured information
   */
  parseError(error: unknown): ParsedError {
    if (error instanceof HttpErrorResponse) {
      return this.parseHttpError(error);
    }

    if (error instanceof Error) {
      return {
        message: error.message || 'An unexpected error occurred',
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
      };
    }

    if (typeof error === 'string') {
      return {
        message: error,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
      };
    }

    if (typeof error === 'object' && error !== null) {
      const errorObj = error as Record<string, unknown>;
      return {
        message: (errorObj['message'] as string) || 'An unexpected error occurred',
        code: errorObj['code'] as string,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
      };
    }

    return {
      message: 'An unexpected error occurred',
      isNetworkError: false,
      isAuthError: false,
      isValidationError: false,
    };
  }

  /**
   * Extract validation messages from error response
   */
  private extractValidationMessage(error: HttpErrorResponse): string {
    const errorBody = error.error;

    // Common API patterns for validation errors
    if (errorBody?.message) {
      return errorBody.message;
    }

    if (errorBody?.errors) {
      // Handle .NET style validation errors
      if (Array.isArray(errorBody.errors)) {
        return errorBody.errors.join('. ');
      }

      // Handle object-style validation errors { field: [messages] }
      if (typeof errorBody.errors === 'object') {
        const messages: string[] = [];
        for (const field of Object.keys(errorBody.errors)) {
          const fieldErrors = errorBody.errors[field];
          if (Array.isArray(fieldErrors)) {
            messages.push(...fieldErrors);
          } else if (typeof fieldErrors === 'string') {
            messages.push(fieldErrors);
          }
        }
        return messages.join('. ') || 'Validation failed';
      }
    }

    return 'Invalid request. Please check your input.';
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: HttpErrorResponse): void {
    // Clear any stored auth data
    localStorage.removeItem('token');

    const message = error.status === 401
      ? 'Your session has expired. Please log in again.'
      : 'You do not have permission to access this resource.';

    this.notification.warning(message, 'Authentication Required');

    // Navigate to login
    this.ngZone.run(() => {
      this.router.navigate(['/authentication/login'], {
        queryParams: { returnUrl: this.router.url }
      });
    });
  }

  /**
   * Log error for debugging/monitoring
   */
  private logError(error: unknown, context?: string): void {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` [${context}]` : '';

    console.error(`[${timestamp}]${contextStr} Error:`, error);

    // In production, you would send this to a logging service
    // Example: this.loggingService.logError(error, context);
  }
}

/**
 * Global Angular ErrorHandler implementation
 * Catches unhandled errors in the application
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private errorHandler = inject(ErrorHandlerService);

  /**
   * Angular internal error codes that should NOT be shown to users
   * These are development-time warnings or internal framework errors
   */
  private readonly angularInternalErrorCodes = [
    'NG0100', // ExpressionChangedAfterItHasBeenCheckedError
    'NG0200', // Circular dependency
    'NG0201', // No provider for
    'NG0300', // Multiple components match
    'NG0301', // Export not found
    'NG0302', // Pipe not found
    'NG0303', // Directive not found
    'NG0500', // Hydration errors
    'NG0501', // Hydration mismatch
    'NG0502', // Hydration missing node
    'NG0503', // Hydration unexpected node
    'NG0504', // Skip hydration
    'NG0505', // Hydration attribute mismatch
    'NG0506', // Hydration text mismatch
    'NG0507', // Hydration sibling mismatch
    'NG0910', // Unsafe value used in URL
    'NG0912', // Component ID collision
  ];

  handleError(error: unknown): void {
    // Unwrap zone.js wrapped errors
    const unwrappedError = this.unwrapError(error);

    // Check if this is an Angular internal error
    if (this.isAngularInternalError(unwrappedError)) {
      // Only log to console, don't show toast notification
      console.error('[Angular Internal Error]', unwrappedError);
      return;
    }

    // Check if this is an HTTP error (these are handled by the interceptor)
    if (unwrappedError instanceof HttpErrorResponse) {
      // HTTP errors are already handled by ErrorInterceptor, just log
      console.error('[HTTP Error]', unwrappedError);
      return;
    }

    // For other unhandled errors, log but don't show toast
    // (showing toasts for every unhandled error is noisy and confusing for users)
    console.error('[Unhandled Error]', unwrappedError);

    // Only show toast for critical errors that users need to know about
    // Uncomment the line below if you want to show toasts for unhandled errors:
    // this.errorHandler.handleError(unwrappedError, 'Unhandled Error');
  }

  /**
   * Check if error is an Angular internal/framework error
   */
  private isAngularInternalError(error: unknown): boolean {
    if (!error) return false;

    // Check error message for Angular error codes
    const errorMessage = this.getErrorMessage(error);

    // Check for NG error codes (e.g., NG0100, NG0200, etc.)
    if (/NG\d{3,4}/.test(errorMessage)) {
      return true;
    }

    // Check for RuntimeError which is Angular's internal error class
    if (error instanceof Error && error.name === 'RuntimeError') {
      return true;
    }

    // Check for specific Angular internal error patterns
    const angularErrorPatterns = [
      'ExpressionChangedAfterItHasBeenCheckedError',
      'NullInjectorError',
      'StaticInjectorError',
      'No provider for',
      'Cannot find module',
      'Template parse errors',
      'Can\'t bind to',
      'is not a known element',
    ];

    return angularErrorPatterns.some(pattern =>
      errorMessage.includes(pattern)
    );
  }

  /**
   * Extract error message from various error types
   */
  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message || error.toString();
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message: unknown }).message);
    }
    return String(error);
  }

  /**
   * Unwrap zone.js wrapped errors
   */
  private unwrapError(error: unknown): unknown {
    // Zone.js wraps errors in a rejection property
    if (error && typeof error === 'object' && 'rejection' in error) {
      return (error as { rejection: unknown }).rejection;
    }
    return error;
  }
}
